import DropdownMenuButton from '../dropdown/dropdownMenuButton';
import { createStyles, makeStyles } from '@mui/styles';
import { Colors, IRupSelectItem } from 'copyright-clearance-center-common-ui';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		container: {
			position: 'relative',
			width: '100%'
		},
		inputContainer: {
			position: 'relative'
		},
		dropdownWrapper: {
			position: 'absolute',
			marginTop: '0.25rem',
			zIndex: 1000,
			width: '100%',
			display: 'flex',
			flexDirection: 'column',
			maxHeight: '8.125rem',
			overflow: 'visible',
			borderRadius: '0.25rem'
		},
		dropdownContent: {
			width: '100%',
			overflow: 'auto',
			flex: '1 1 auto',
			position: 'relative',
			borderTopLeftRadius: '0.25rem',
			borderTopRightRadius: '0.25rem',
			backgroundColor: '#ffffff'
		},
		dropdownMenuButtonOverride: {
			padding: '0.5rem 0.75rem !important',
			lineHeight: '1.1rem !important',
			fontSize: '0.875rem !important',
			fontWeight: '400 !important',
			color: `${Colors.neutral900} !important`,
			'&.selected': {
				backgroundColor: `${Colors.neutral300} !important`
			}
		},
		itemText: {
			display: 'inline-block',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap'
		},
		loadingIndicator: {
			padding: '0.5rem',
			textAlign: 'center',
			color: Colors.neutral700,
			fontStyle: 'italic'
		},
		statusMessage: {
			padding: '0.375rem 0.5rem',
			fontSize: '0.75rem',
			borderTop: `1px solid ${Colors.neutral300}`,
			backgroundColor: Colors.neutral50,
			textAlign: 'center',
			color: Colors.neutral700,
			width: '100%',
			flex: '0 0 auto'
		},
		emptyState: {
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			justifyContent: 'center',
			minHeight: '1.875rem',
			pointerEvents: 'none'
		}
	})
);

interface IInputWithSearchableDropdownProps {
	id?: string;
	label: string;
	placeholder?: string;
	onSelect: (item: IRupSelectItem) => void;
	initialValue?: string;
	testId?: string;
	disabled?: boolean;
	className?: string;
	fetchItems: (
		searchText: string,
		page: number,
		pageSize: number
	) => Promise<{
		items: IRupSelectItem[];
		hasMore: boolean;
		total: number;
	}>;
	pageSize?: number;
}

const InputWithSearchableDropdown: React.FC<IInputWithSearchableDropdownProps> = ({
	id,
	label,
	placeholder = '',
	onSelect,
	initialValue = '',
	testId = 'searchable-dropdown',
	disabled = false,
	className = '',
	fetchItems,
	pageSize = 20
}) => {
	const classes = useStyles();

	const [searchText, setSearchText] = useState('');
	const [displayedItems, setDisplayedItems] = useState<IRupSelectItem[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState<IRupSelectItem | null>(null);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [total, setTotal] = useState(0);

	const isLoadingMoreRef = useRef(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputId = id ?? `${testId}-input`;
	const isMountedRef = useRef(true);
	const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
	const currentSearchQueryRef = useRef('');
	const isReopeningWithSelectionRef = useRef(false);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
			if (typingTimerRef.current) {
				clearTimeout(typingTimerRef.current);
			}
		};
	}, []);

	const performSearch = useCallback(
		async (query: string, force = false) => {
			if (!isMountedRef.current || (loading && !force)) {
				return;
			}

			const actualQuery = isReopeningWithSelectionRef.current ? '' : query;
			currentSearchQueryRef.current = actualQuery;
			setLoading(true);

			try {
				const result = await fetchItems(actualQuery, 1, pageSize);

				if (!isMountedRef.current) {
					return;
				}

				setDisplayedItems(result.items);
				setHasMore(result.hasMore);
				setTotal(result.total);
				setPage(1);
			} catch (error) {
				if (!isMountedRef.current) {
					return;
				}

				console.error('Error fetching items', error);
				setDisplayedItems([]);
			} finally {
				if (isMountedRef.current) {
					setLoading(false);
				}
			}
		},
		[fetchItems, pageSize, loading]
	);

	const loadMoreItems = useCallback(async () => {
		if (!hasMore || isLoadingMoreRef.current || loading || !isMountedRef.current) {
			return;
		}

		isLoadingMoreRef.current = true;
		setLoading(true);

		try {
			const nextPage = page + 1;
			const result = await fetchItems(currentSearchQueryRef.current, nextPage, pageSize);

			if (!isMountedRef.current) {
				return;
			}

			setDisplayedItems(prev => [...prev, ...result.items]);
			setHasMore(result.hasMore);
			setTotal(result.total);
			setPage(nextPage);
		} catch (error) {
			if (isMountedRef.current) {
				console.error('Error loading more items', error);
			}
		} finally {
			if (isMountedRef.current) {
				setLoading(false);
				isLoadingMoreRef.current = false;
			}
		}
	}, [fetchItems, page, hasMore, pageSize, loading]);

	const checkScrollPosition = useCallback(
		debounce((element: HTMLElement) => {
			const { scrollTop, scrollHeight, clientHeight } = element;
			const scrollBottom = scrollHeight - scrollTop - clientHeight;

			if (scrollBottom < 30) {
				loadMoreItems();
			}
		}, 100),
		[loadMoreItems]
	);

	useEffect(() => {
		return () => {
			checkScrollPosition?.cancel();
		};
	}, [checkScrollPosition]);

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const target = e.currentTarget;
		checkScrollPosition(target);
	};

	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!isOpen) {
			if (e.key === 'ArrowDown') {
				setIsOpen(true);
				e.preventDefault();
			}

			return;
		}

		let key = e.key;
		if (key === 'Tab') {
			e.preventDefault();
			key = e.shiftKey ? 'ArrowUp' : 'ArrowDown';
		}

		switch (key) {
			case 'ArrowDown':
				e.preventDefault();
				if (displayedItems.length > 0) {
					const currentIndex = selectedItem
						? displayedItems.findIndex(item => item.value === selectedItem.value)
						: -1;
					const nextIndex = Math.min(currentIndex + 1, displayedItems.length - 1);

					if (nextIndex !== currentIndex || nextIndex === -1) {
						setSelectedItem(displayedItems[nextIndex === -1 ? 0 : nextIndex]);
						setTimeout(() => {
							scrollSelectedItemIntoView(nextIndex === -1 ? 0 : nextIndex);
						}, 0);
					}
				}

				break;

			case 'ArrowUp':
				e.preventDefault();
				if (displayedItems.length > 0) {
					const currentIndex = selectedItem
						? displayedItems.findIndex(item => item.value === selectedItem.value)
						: 0;
					const prevIndex = Math.max(currentIndex - 1, 0);

					if (prevIndex !== currentIndex) {
						setSelectedItem(displayedItems[prevIndex]);
						setTimeout(() => {
							scrollSelectedItemIntoView(prevIndex);
						}, 0);
					}
				}

				break;

			case 'Enter':
				e.preventDefault();
				handleSelectItem(selectedItem);
				break;
		}
	};

	const scrollSelectedItemIntoView = (index: number) => {
		if (!dropdownRef.current) {
			return;
		}

		const items = dropdownRef.current.querySelectorAll(`button[data-test-id^="${testId}-item-"]`);

		if (index >= 0 && index < items.length) {
			const dropdownContainer = dropdownRef.current;
			const itemElement = items[index] as HTMLElement;

			const containerTop = dropdownContainer.scrollTop;
			const containerHeight = dropdownContainer.clientHeight;
			const itemTop = itemElement.offsetTop;
			const itemHeight = itemElement.offsetHeight;

			if (itemTop < containerTop) {
				dropdownContainer.scrollTop = itemTop;
			} else if (itemTop + itemHeight > containerTop + containerHeight) {
				dropdownContainer.scrollTop = itemTop + itemHeight - containerHeight;
			}
		}
	};

	useEffect(() => {
		if (initialValue) {
			(async () => {
				const result = await fetchItems(`id:${initialValue}`, 1, 1);

				if (!isMountedRef.current) {
					return;
				}

				if (result.items.length > 0) {
					setSelectedItem(result.items[0]);
					setSearchText(result.items[0].text);
				}
			})();
		}
	}, [initialValue, fetchItems]);

	useEffect(() => {
		if (!isOpen) {
			isReopeningWithSelectionRef.current = false;
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				isReopeningWithSelectionRef.current = false;

				if (selectedItem && inputRef.current) {
					setSearchText(selectedItem.text);
					inputRef.current.value = selectedItem.text;
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchText(value);
		setIsOpen(true);
		isReopeningWithSelectionRef.current = false;

		if (value === '' && selectedItem !== null) {
			setSelectedItem(null);
		}

		if (typingTimerRef.current) {
			clearTimeout(typingTimerRef.current);
		}

		typingTimerRef.current = setTimeout(() => {
			if (isMountedRef.current) {
				performSearch(value, true);
			}
		}, 500);
	};

	const getStatusMessage = (): string => {
		if (loading && displayedItems.length === 0) {
			return localize('Loading...');
		}

		if (total <= 0) {
			return localize('No Matches');
		}

		return localize('Items <b>1</b>-<b>{0}</b> out of <b>{1}</b>', displayedItems.length, total);
	};

	const handleSelectItem = (selectedOption: IRupSelectItem) => {
		const optionItem = selectedOption ?? null;
		const optionItemText = selectedOption?.text ?? '';

		setSelectedItem(optionItem);
		setSearchText(optionItemText);

		if (inputRef.current) {
			inputRef.current.value = optionItemText;
		}

		setIsOpen(false);
		onSelect?.(optionItem);
	};

	const handleInputFocus = () => {
		if (disabled) {
			return;
		}

		if (!isOpen) {
			setIsOpen(true);

			if (selectedItem) {
				isReopeningWithSelectionRef.current = true;
				performSearch('', true);
			} else if (!loading) {
				performSearch(searchText, true);
			}
		}
	};

	return (
		<div ref={containerRef} className={classes.container} data-test-id={testId}>
			<label htmlFor={inputId} className={`${className}`}>
				{label}
			</label>
			<div className={classes.inputContainer}>
				<input
					ref={inputRef}
					id={inputId}
					className="rup-input input-wrapper"
					type="text"
					placeholder={placeholder}
					value={searchText}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onKeyDown={handleInputKeyDown}
					disabled={disabled}
					data-test-id={`${testId}-input`}
				/>
			</div>

			{isOpen && !disabled && (
				<div className={`${classes.dropdownWrapper} rup-elevation rup-elevation--8`}>
					<div
						ref={dropdownRef}
						className={`${classes.dropdownContent} rup-dropdown__content--helper-states`}
						onScroll={handleScroll}
					>
						{displayedItems.length > 0 &&
							displayedItems.map(item => (
								<DropdownMenuButton
									key={item.value}
									onClick={() => handleSelectItem(item)}
									testId={`${testId}-item-${item.value}`}
									className={`${classes.dropdownMenuButtonOverride} ${
										selectedItem?.value === item.value ? 'selected' : ''
									}`}
								>
									<span className={classes.itemText}>{item.text}</span>
								</DropdownMenuButton>
							))}

						{loading && <div className={classes.loadingIndicator}>Loading...</div>}

						{displayedItems.length === 0 && !loading && <div className={classes.emptyState}></div>}
					</div>

					<div
						className={classes.statusMessage}
						data-test-id={`${testId}-status`}
						dangerouslySetInnerHTML={{ __html: getStatusMessage() }}
					/>
				</div>
			)}
		</div>
	);
};

export default InputWithSearchableDropdown;
